import { useState, useCallback } from "react";
import { format } from "date-fns";
import { MeetingFormData, CalendarPlatform } from "../types/meeting.js";
import {
  parseEmailsFromCsv,
  emailsToCsv,
  mergeEmailLists,
} from "../utils/meetingFormUtils.js";

interface UseMeetingFormStateReturn {
  formData: MeetingFormData;
  setFormData: (data: MeetingFormData) => void;
  handleInputChange: (field: keyof MeetingFormData, value: string | number | boolean) => void;
  handleAddTeamMember: (email: string) => void;
  handleRemoveAttendee: (emailToRemove: string) => void;
  handleAttendeeInputChange: (value: string) => void;
  handleAttendeeInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  addAttendeeFromInput: () => void;
  currentAttendees: string[];
  attendeeInput: string;
  setAttendeeInput: (value: string) => void;
}

export function useMeetingFormState(
  initialDate: Date = new Date(),
  initialDuration: number = 60,
  customerName?: string,
  initialPlatform: CalendarPlatform = "google",
  /** Comma-separated attendee emails to pre-populate (e.g. the contact being scheduled). */
  initialAttendeesCsv?: string
): UseMeetingFormStateReturn {
  const [formData, setFormData] = useState<MeetingFormData>({
    title: customerName ? `WTC <> ${customerName}: Meeting` : "WTC Meeting",
    description: "",
    location: "",
    startDate: format(initialDate, "yyyy-MM-dd"),
    startTime: format(initialDate, "HH:mm"),
    duration: initialDuration,
    attendees: emailsToCsv(parseEmailsFromCsv(initialAttendeesCsv ?? "")),
    platform: initialPlatform,
    isOnline: false,
  });

  const [attendeeInput, setAttendeeInput] = useState("");

  const handleInputChange = useCallback(
    (field: keyof MeetingFormData, value: string | number | boolean) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    []
  );

  const handleAddTeamMember = useCallback(
    (email: string) => {
      const current = parseEmailsFromCsv(formData.attendees);
      if (!current.includes(email)) {
        const merged = mergeEmailLists(current, [email]);
        handleInputChange("attendees", emailsToCsv(merged));
      }
    },
    [formData.attendees, handleInputChange]
  );

  const handleRemoveAttendee = useCallback(
    (emailToRemove: string) => {
      const current = parseEmailsFromCsv(formData.attendees);
      const filtered = current.filter((email) => email !== emailToRemove);
      handleInputChange("attendees", emailsToCsv(filtered));
    },
    [formData.attendees, handleInputChange]
  );

  const handleAttendeeInputChange = useCallback((value: string) => {
    setAttendeeInput(value);
  }, []);

  const addAttendeeFromInput = useCallback(() => {
    if (!attendeeInput.trim()) return;
    const additions = parseEmailsFromCsv(attendeeInput);
    const current = parseEmailsFromCsv(formData.attendees);
    const merged = mergeEmailLists(current, additions);
    handleInputChange("attendees", emailsToCsv(merged));
    setAttendeeInput("");
  }, [attendeeInput, formData.attendees, handleInputChange]);

  const handleAttendeeInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        addAttendeeFromInput();
      }
    },
    [addAttendeeFromInput]
  );

  const currentAttendees = parseEmailsFromCsv(formData.attendees);

  return {
    formData,
    setFormData,
    handleInputChange,
    handleAddTeamMember,
    handleRemoveAttendee,
    handleAttendeeInputChange,
    handleAttendeeInputKeyDown,
    addAttendeeFromInput,
    currentAttendees,
    attendeeInput,
    setAttendeeInput,
  };
}
